import React from "react";

import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons/faExternalLinkAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { EntityType, EntityTypesSupportingRoutes } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import useDefaultSourceObjectKey from "@app/util/hook/useDefaultSourceObjectKey";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";

import styles from "./Count.module.scss";

interface Props {
  count: number;
  entityType: EntityTypesSupportingRoutes;
}

const messages = defineMessages({
  [EntityType.COMPANY]: {
    id: "leadFinder.resultsModal.count.companies",
    defaultMessage:
      "{sourceObject, select, standard {{count, plural, one {Company} other {Companies}}} leadCompanies {{count, plural, one {Lead} other {Leads}}} siteCompanies {{count, plural, one {Site} other {Sites}}} projectCompanies {{count, plural, one {Project} other {Projects}}} shopCompanies {{count, plural, one {Shop} other {Shops}}} other {{label}}}{br}added",
    description: "Lead Finder results counter, named after the source object",
  },
  [EntityType.PERSON]: {
    id: "leadFinder.resultsModal.count.people",
    defaultMessage:
      "{sourceObject, select, standard {{count, plural, one {Person} other {People}}} partnerPeople {{count, plural, one {Partner} other {Partners}}} leadPeople {{count, plural, one {Lead} other {Leads}}} staffMemberPeople {{count, plural, one {Staff Member} other {Staff Members}}} leadPersonnel {{count, plural, one {Lead Personnel} other {Lead Personnel}}} other {{label}}}{br}added",
    description: "Lead Finder results counter, named after the source object",
  },
});

const tooltips = defineMessages({
  [EntityType.COMPANY]: {
    id: "leadFinder.resultsModal.count.tooltip.companies",
    defaultMessage:
      "View New {sourceObject, select, standard {Companies} leadCompanies {Leads} siteCompanies {Sites} projectCompanies {Projects} shopCompanies {Shops} other {{label}}}",
    description: "Lead Finder results counter tooltip, named after the source object",
  },
  [EntityType.PERSON]: {
    id: "leadFinder.resultsModal.count.tooltip.people",
    defaultMessage:
      "View New {sourceObject, select, standard {People} partnerPeople {Partners} leadPeople {Leads} staffMemberPeople {Staff Members} leadPersonnel {Lead Personnel} other {{label}}}",
    description: "Lead Finder results counter tooltip, named after the source object",
  },
});

const Count = ({ count, entityType }: Props) => {
  const intl = useIntl();

  const defaultSourceObject = useDefaultSourceObjectKey(entityType);
  const messageArgs = useSourceObjectMessageArgs(entityType, defaultSourceObject);

  const entityPath = entityType === EntityType.COMPANY ? Path.COMPANY : Path.PEOPLE;
  const actionLink = `${entityPath}?sort=-createdAt`;

  return (
    <div className={styles.container}>
      <span className={styles.number}>{count}</span>

      <span className={styles.note}>
        <Tooltip placement="bottom" title={intl.formatMessage(tooltips[entityType], messageArgs)}>
          <Link target="_blank" to={actionLink}>
            <Button
              className={styles.btn}
              rightIcon={
                <FontAwesomeIcon className={styles.externalLinkIcon} icon={faExternalLinkAlt} />
              }
              type="link"
            >
              {intl.formatMessage(messages[entityType], {
                ...messageArgs,
                br: <br />,
                count,
              })}
            </Button>
          </Link>
        </Tooltip>
      </span>
    </div>
  );
};

export default Count;
